import React, { useState, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import queryString from 'query-string';
import moment from 'moment';

import { useTheme } from '@material-ui/core/styles';

import { setPortal } from 'modules/portal';
import { makeRowColumn, getHeadObj, dataConverter } from 'lib/commonLib';
import { returnMcReport } from 'lib/api/return';

import IconAreaWrap from 'components/molecules/MbTable/IconAreaWrap';
import InfoTitleArea from 'components/molecules/MbTable/InfoTitleArea';
import VariousCell from 'components/molecules/MbTable/VariousCell';
import NewTableContainer from 'components/molecules/NewTable';
import NewFilterWrap from 'components/molecules/NewFilterWrap';
import NewCommonFilter from 'components/organisms/NewCommonFilter';

const McStatistics = ({ location }) => {
  const theme = useTheme();

  // 통계보고서 - 반송율통계 - 광고주별통계 adverId, sDate, eDate
  // 확인불가 mediaId
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { portal } = useSelector(({ portal }) => ({
    portal: portal,
  }));
  const isPopPage = queryString.parse(location.search).isPop === '1';
  const isAdverId = queryString.parse(location.search).adverId !== undefined;

  let initParams = {
    sort: 'desc',
    order: 'ADVRTS_AMT',
    pltfomTpCode: 'all',
    sDate: moment().add(-30, 'day').format('YYYYMMDD'),
    eDate: moment().format('YYYYMMDD'),
  };
  const convertKeys = ['mediaId', 'adverId', 'sDate', 'eDate'];
  const newInitParams = dataConverter(
    queryString.parse(location.search),
    convertKeys
  );
  initParams = {
    ...initParams,
    ...newInitParams,
  };
  const borderRangeStart = isAdverId ? 13 : 14;
  const borderRangeEnd = isAdverId ? 17 : 18;

  const [initialization, setInitialization] = useState(false);
  const [params, setParams] = useState(initParams);

  useEffect(() => {
    if (initialization === true) {
      setInitialization(false);
    }
  }, [initialization]);

  const handlePortalUrl = (url) => {
    let newPortal = portal.portal;
    let popIndex = newPortal.length;
    newPortal[popIndex] = { url: url, cnt: popIndex };
    dispatch(setPortal(newPortal));
  };

  /**
   * 필터 초기화
   */
  const handleOnReset = () => {
    if (JSON.stringify(params) !== JSON.stringify(initParams)) {
      setInitialization(true);
      setParams(initParams);
      if (isPopPage && window.location.search !== '?isPop=1') {
        window.location.replace(window.location.pathname + '?isPop=1');
      }
    }
  };

  /**
   * 페이지 파라미터를 관리하는 params을 수정하는 함수
   * @param newParams
   */
  const getParam = (newParams) => {
    if (JSON.stringify(params) !== JSON.stringify(newParams)) {
      setParams(newParams);
    }
  };

  // header 및 body 컬럼 커스텀
  const customized = {
    MEDIA_SCRIPT_NO: { merged: true },
    SCRIPT_NAME: {
      makeBody: (rowObj, headsMap) => {
        let resultComponent;
        const showWindowUrl =
          '/report/retrn/newadver?sDate=' +
          params.sDate +
          '&eDate=' +
          params.eDate +
          '&mediaScriptNo=' +
          rowObj['MEDIA_SCRIPT_NO'] +
          '&uri=/report/retrn/newadver&isPop=1';

        isPopPage
          ? (resultComponent = rowObj['SCRIPT_NAME'])
          : (resultComponent = (
              <IconAreaWrap
                text={rowObj['SCRIPT_NAME']}
                icon={'faExternalLink'}
                toolTipTitle={t('해당 지면의 광고주별 통계')}
                onClick={(e) => handlePortalUrl(showWindowUrl)}
              />
            ));

        return resultComponent;
      },
    },
    PAR_EPRS_CNT: {
      makeBody: (rowObj, headsMap) => {
        const bodyItem = getHeadObj(headsMap, 'PAR_EPRS_CNT');
        return makeRowColumn(
          rowObj['PAR_EPRS_CNT'],
          bodyItem.type,
          bodyItem.format
        );
      },
    },
    CLICK_CNT: {
      makeHead: (headsMap) => {
        return (
          <>
            {getHeadObj(headsMap, 'CLICK_CNT').label}
            <br />({getHeadObj(headsMap, 'TOT_CLICK_CNT').label.replace(' ','\u00A0')})
          </>
        );
      },
      makeBody: (rowObj, headsMap) => {
        const clickCnt = getHeadObj(headsMap, 'CLICK_CNT');
        const clickCount = getHeadObj(headsMap, 'TOT_CLICK_CNT');
        return (
          <>
            {makeRowColumn(rowObj['CLICK_CNT'], clickCnt.type, clickCnt.format)}
            <br />(
            {makeRowColumn(
              rowObj['TOT_CLICK_CNT'],
              clickCount.type,
              clickCount.format
            )}
            )
          </>
        );
      },
    },
    TOT_CLICK_CNT: { merged: true },
    RETRN_00_CNT: {
      makeHead: (headsMap) => {
        return (
            <InfoTitleArea
                title={getHeadObj(headsMap, 'RETRN_00_CNT').label}
                tooltipTitle={'클릭 후 1초 미만에 이탈하거나 스크립트 작동이 안한 무효 클릭'}
                iconType="fainfoCircle"
            />
        );
      },
      makeBody: (rowObj, headsMap) => {
        const retrnRate = getHeadObj(headsMap, 'RETRN_00_CNT');
        return makeRowColumn(
            rowObj['RETRN_00_CNT'],
            retrnRate.type,
            retrnRate.format
        );
      },
    },
    RETRN_01_CNT: {
      makeHead: (headsMap) => {
        return (
            <InfoTitleArea
                title={getHeadObj(headsMap, 'RETRN_01_CNT').label}
                tooltipTitle={'클릭 후 1초가 지났지만 10초 이전에 나간 무효 클릭'}
                iconType="fainfoCircle"
            />
        );
      },
      makeBody: (rowObj, headsMap) => {
        const retrnRate = getHeadObj(headsMap, 'RETRN_01_CNT');
        return makeRowColumn(
            rowObj['RETRN_01_CNT'],
            retrnRate.type,
            retrnRate.format
        );
      },
    },
    AVAL_CNT: {
      makeHead: (headsMap) => {
        return (
            <InfoTitleArea
                title={getHeadObj(headsMap, 'AVAL_CNT').label}
                tooltipTitle={
                  '체류시간 10초 이상, pv 2회인 유효 클릭'
                }
                iconType="fainfoCircle"
            />
        );
      },
      makeBody: (rowObj, headsMap) => {
        const avalRate = getHeadObj(headsMap, 'AVAL_CNT');
        return makeRowColumn(
            rowObj['AVAL_CNT'],
            avalRate.type,
            avalRate.format
        );
      },
    },
    ADVRTS_AMT: {
      makeBody: (rowObj, headsMap) => {
        const advrtsAmt = getHeadObj(headsMap, 'ADVRTS_AMT');
        const trgtAdvrtsAmt = getHeadObj(headsMap, 'TRGT_ADVRTS_AMT');
        const mcPoint = getHeadObj(headsMap, 'MC_POINT');
        return (
          <>
            {makeRowColumn(
              rowObj['ADVRTS_AMT'],
              advrtsAmt.type,
              advrtsAmt.format
            )}{' '}
            <span style={{ color: theme.palette.common.black }}>
              (
              {makeRowColumn(
                rowObj['TRGT_ADVRTS_AMT'],
                trgtAdvrtsAmt.type,
                trgtAdvrtsAmt.format
              )}
              )
              {!isAdverId && (
                  <>
                    <br />
                    {makeRowColumn(rowObj['MC_POINT'], mcPoint.type, mcPoint.format)}
                  </>
              )}
            </span>
          </>
        );
      },
    },
    TRGT_ADVRTS_AMT: { merged: true },
    MC_POINT: { merged: true },
    MC_POINT_RATE: { merged: isAdverId ? true : false }, // 광고주아이디 필터시 지급률 미노출
    AVG_ORDER_AMT: {
      makeHead: (headsMap) => {
        return (
          <>
            {getHeadObj(headsMap, 'AVG_ORDER_AMT').label}
            <br />({getHeadObj(headsMap, 'CONV_AMT').label})
          </>
        );
      },
      makeBody: (rowObj, headsMap) => {
        const avgOrderAmt = getHeadObj(headsMap, 'AVG_ORDER_AMT');
        const convAmt = getHeadObj(headsMap, 'CONV_AMT');
        return (
          <>
            {makeRowColumn(
              rowObj['AVG_ORDER_AMT'],
              avgOrderAmt.type,
              avgOrderAmt.format
            )}
            <br />
            <div className={'black'}>
              ({makeRowColumn(rowObj['CONV_AMT'], convAmt.type, convAmt.format)}
              )
            </div>
          </>
        );
      },
    },
    CONV_AMT: { merged: true },
    MOB_SESSION_ROAS: {
      makeHead: (headsMap) => {
        return {
          content: (
            <InfoTitleArea
              title={t('결제매출') + '(' + t('쇼핑') + ')'}
              tooltipType="MOB_SESSION_ROAS"
              iconType="fainfoCircle"
            />
          ),
          orderFlag: true,
        };
      },
      makeBody: (rowObj, headsMap) => {
        const mobSessionRoas = getHeadObj(headsMap, 'MOB_SESSION_ROAS');
        const trgtMobSessionRoas = getHeadObj(
          headsMap,
          'TRGT_MOB_SESSION_ROAS'
        );
        const mobSessionOrderAmt = getHeadObj(
          headsMap,
          'MOB_SESSION_ORDER_AMT'
        );
        return (
          <VariousCell
            type="TRGT"
            item1={makeRowColumn(
              rowObj['MOB_SESSION_ROAS'],
              mobSessionRoas.type,
              mobSessionRoas.format
            )}
            item2={makeRowColumn(
              rowObj['TRGT_MOB_SESSION_ROAS'],
              trgtMobSessionRoas.type,
              trgtMobSessionRoas.format
            )}
            item3={makeRowColumn(
              rowObj['MOB_SESSION_ORDER_AMT'],
              mobSessionOrderAmt.type,
              mobSessionOrderAmt.format
            )}
          />
        );
      },
    },
    TRGT_MOB_SESSION_ROAS: { merged: true },
    MOB_SESSION_ORDER_AMT: { merged: true },
    SESSION_ROAS: {
      makeHead: (headsMap) => {
        return {
          content: (
            <InfoTitleArea
              title={
                getHeadObj(headsMap, 'SESSION_ORDER_AMT').label +
                '(' +
                t('쇼핑') +
                ')'
              }
              tooltipType="SESSION_ROAS"
              iconType="fainfoCircle"
            />
          ),
          orderFlag: true,
        };
      },
      makeBody: (rowObj, headsMap) => {
        const sessionRoas = getHeadObj(headsMap, 'SESSION_ROAS');
        const trgtSessionRoas = getHeadObj(headsMap, 'TRGT_SESSION_ROAS');
        const sessionOrderAmt = getHeadObj(headsMap, 'SESSION_ORDER_AMT');
        return (
          <VariousCell
            bold
            type="TRGT"
            item1={makeRowColumn(
              rowObj['SESSION_ROAS'],
              sessionRoas.type,
              sessionRoas.format
            )}
            item2={makeRowColumn(
              rowObj['TRGT_SESSION_ROAS'],
              trgtSessionRoas.type,
              trgtSessionRoas.format
            )}
            item3={makeRowColumn(
              rowObj['SESSION_ORDER_AMT'],
              sessionOrderAmt.type,
              sessionOrderAmt.format
            )}
          />
        );
      },
    },
    TRGT_SESSION_ROAS: { merged: true },
    SESSION_ORDER_AMT: { merged: true },
    DIRECT_ROAS: {
      makeHead: (headsMap) => {
        return (
          <InfoTitleArea
            title={
              getHeadObj(headsMap, 'DIRECT_ORDER_AMT').label +
              '(' +
              t('쇼핑') +
              ')'
            }
            tooltipType="DIRECT_ROAS"
            iconType="fainfoCircle"
          />
        );
      },
      makeBody: (rowObj, headsMap) => {
        const directRoas = getHeadObj(headsMap, 'DIRECT_ROAS');
        const trgtDirectRoas = getHeadObj(headsMap, 'TRGT_DIRECT_ROAS');
        const directOrderAmt = getHeadObj(headsMap, 'DIRECT_ORDER_AMT');
        return (
          <VariousCell
            type="TRGT"
            item1={makeRowColumn(
              rowObj['DIRECT_ROAS'],
              directRoas.type,
              directRoas.format
            )}
            item2={makeRowColumn(
              rowObj['TRGT_DIRECT_ROAS'],
              trgtDirectRoas.type,
              trgtDirectRoas.format
            )}
            item3={makeRowColumn(
              rowObj['DIRECT_ORDER_AMT'],
              directOrderAmt.type,
              directOrderAmt.format
            )}
          />
        );
      },
    },
    TRGT_DIRECT_ROAS: { merged: true },
    DIRECT_ORDER_AMT: { merged: true },
    ROAS: {
      makeHead: (headsMap) => {
        return (
          <InfoTitleArea
            title={
              getHeadObj(headsMap, 'ORDER_AMT').label + '(' + t('쇼핑') + ')'
            }
            tooltipType="ROAS"
            iconType="fainfoCircle"
          />
        );
      },
      makeBody: (rowObj, headsMap) => {
        const roas = getHeadObj(headsMap, 'ROAS');
        const trgtRoas = getHeadObj(headsMap, 'TRGT_ROAS');
        const orderAmt = getHeadObj(headsMap, 'ORDER_AMT');
        return (
          <VariousCell
            type="TRGT"
            item1={makeRowColumn(rowObj['ROAS'], roas.type, roas.format)}
            item2={makeRowColumn(
              rowObj['TRGT_ROAS'],
              trgtRoas.type,
              trgtRoas.format
            )}
            item3={makeRowColumn(
              rowObj['ORDER_AMT'],
              orderAmt.type,
              orderAmt.format
            )}
          />
        );
      },
    },
    TRGT_ROAS: { merged: true },
    ORDER_AMT: { merged: true },
    ECPM: {
      makeHead: (headsMap) => {
        return (
          <InfoTitleArea
            title={
              <>
                {getHeadObj(headsMap, 'ECPM').label}
                <br />
                ({t('쇼핑')})
              </>
            }
            tooltipType="ECPM"
            iconType="fainfoCircle"
          />
        );
      },
      makeBody: (rowObj, headsMap) => {
        const ecpm = getHeadObj(headsMap, 'ECPM');
        const trgtEcpm = getHeadObj(headsMap, 'TRGT_ECPM');
        return (
          <>
            {makeRowColumn(rowObj['ECPM'], ecpm.type, ecpm.format)}
            <br />(
            {makeRowColumn(rowObj['TRGT_ECPM'], trgtEcpm.type, trgtEcpm.format)}
            )
          </>
        );
      },
    },
    TRGT_ECPM: { merged: true },
  };

  return (
    <>
      <NewFilterWrap handleOnReset={handleOnReset}>
        <NewCommonFilter
          initialization={initialization}
          getParam={getParam}
          params={params}
          useExternalFilter={false}
        />
      </NewFilterWrap>
      <NewTableContainer
        dataReqPromise={returnMcReport}
        params={params}
        getParam={getParam}
        customized={customized}
        useTotal={false}
        toolTip={t(
          '아래 통계는 반송/유효 측정이 가능한 지면(아이커버/M커버/외부연동/APP 제외)에서 집계된 데이터로 전체 통계와 상이합니다.'
        )}
        borderRangeStart={borderRangeStart}
        borderRangeEnd={borderRangeEnd}
      />
    </>
  );
};

export default withRouter(McStatistics);
